import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/hooks/useAuth';
import { servicesService, collaborateursService, promptTemplatesService, appelsOffresService, evaluationsService, criteresService } from '@/services';
import WorkflowBar from "@/components/WorkflowBar";
import { ArrowLeft, ArrowRight, Save, FileText, Target, Settings } from "lucide-react";

import { AOGeneralStep } from "./steps/AOGeneralStep";
import { AOEvaluationsStep } from "./steps/AOEvaluationsStep";
import { AOConfigStep } from "./steps/AOConfigStep";

export interface AOFormData {
  titre: string;
  reference: string;
  description: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  ao_manager: string;
  interlocuteur_da: string;
  lien_ao: string;
  seuil_shortlist: number;
  seuil_consider: number;
  service_responsable_id?: string;
  prompt_template_id?: string;
  statut: string;
}

export interface Evaluation {
  id?: string;
  ao_id: string;
  nom: string;
  description: string;
  ordre: number;
  ponderation_globale_pct: number;
}

export interface CritereEvaluation {
  id?: string;
  evaluation_id?: string;
  nom: string;
  description: string;
  ponderation_pct: number;
  contenu_html: string;
  ordre: number;
}

export interface Service {
  id: string;
  nom: string;
}

export interface Collaborateur {
  id: string;
  nom: string;
  service_id?: string;
}

export interface PromptTemplate {
  id: string;
  name: string;
  type: string;
  template_content: string;
}

const steps = [
  {
    id: "general",
    title: "Informations",
    description: "Données générales",
    icon: FileText,
    status: 'current' as const
  },
  {
    id: "evaluations", 
    title: "Évaluations",
    description: "Critères et pondérations",
    icon: Target,
    status: 'upcoming' as const
  },
  {
    id: "config",
    title: "Configuration", 
    description: "Paramètres",
    icon: Settings,
    status: 'upcoming' as const
  }
];

export function AOWizard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  // Form data state
  const [formData, setFormData] = useState<AOFormData>({
    titre: "",
    reference: "",
    description: "",
    date_limite: "",
    date_publication: "",
    date_debut_redaction_ao: "",
    date_qr_soumissionnaires: "",
    date_reponses_qr: "",
    date_analyse_offres: "",
    date_debut_soutenance: "",
    date_fin_soutenance: "",
    date_adjudication: "",
    date_contractualisation: "",
    ao_manager: "",
    interlocuteur_da: "",
    lien_ao: "",
    seuil_shortlist: 75,
    seuil_consider: 60,
    service_responsable_id: "",
    prompt_template_id: "",
    statut: "en cours",
  });

  const [evaluations, setEvaluations] = useState<Evaluation[]>([
    {
      ao_id: id || '',
      nom: "Évaluation générale",
      description: "Évaluation par défaut",
      ordre: 1,
      ponderation_globale_pct: 100
    }
  ]);
  
  const [criteresMap, setCriteresMap] = useState<Record<number, CritereEvaluation[]>>({});

  const [services, setServices] = useState<Service[]>([]);
  const [collaborateurs, setCollaborateurs] = useState<Collaborateur[]>([]);
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);

  // Auto-save functionality
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadServices(),
        loadCollaborateurs(),
        loadPromptTemplates()
      ]);
      
      if (isEditMode && id) {
        await loadAppelOffre();
      }
    };
    
    loadData();
  }, [id, isEditMode]);

  // Fonction pour gérer les changements de critères
  const handleCriteresChange = useCallback((evaluationIndex: number, criteres: any[]) => {
    console.log('AOWizard: Critères reçus pour évaluation', evaluationIndex, ':', criteres);
    setCriteresMap(prev => {
      const newMap = {
        ...prev,
        [evaluationIndex]: criteres
      };
      console.log('AOWizard: CriteresMap final:', newMap);
      return newMap;
    });
  }, []);

  // Auto-save effect pour le formulaire
  useEffect(() => {
    if (!autoSaveEnabled || !isEditMode || currentStep !== 0) return;

    const timeoutId = setTimeout(() => {
      handleAutoSave();
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, autoSaveEnabled, isEditMode, currentStep]);

  // Auto-save effect pour les évaluations et critères
  useEffect(() => {
    console.log('AOWizard: Auto-save check - autoSaveEnabled:', autoSaveEnabled, 'isEditMode:', isEditMode, 'currentStep:', currentStep);
    
    if (!autoSaveEnabled || !isEditMode) {
      console.log('AOWizard: Auto-save désactivé ou pas en mode édition');
      return;
    }
    
    // Auto-save pour les évaluations et critères sur n'importe quelle étape si on a des critères
    if (Object.keys(criteresMap).length > 0) {
      console.log('AOWizard: Auto-save évaluations/critères. CriteresMap:', criteresMap);
      const timeoutId = setTimeout(() => {
        handleAutoSave();
      }, 2000);

      return () => clearTimeout(timeoutId);
    } else {
      console.log('AOWizard: Pas de critères à sauvegarder, criteresMap vide');
    }
  }, [evaluations, criteresMap, autoSaveEnabled, isEditMode]);

  const loadServices = async () => {
    try {
      const data = await servicesService.getAll();
      setServices(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
    }
  };

  const loadCollaborateurs = async () => {
    try {
      const data = await collaborateursService.getAll();
      // Map to expected format
      setCollaborateurs((data || []).map((c: any) => ({
        id: c.id,
        nom: c.nom,
        service_id: c.serviceId
      })));
    } catch (error) {
      console.error('Erreur lors du chargement des collaborateurs:', error);
    }
  };

  const loadPromptTemplates = async () => {
    try {
      const allTemplates = await promptTemplatesService.getAll();
      // Filter by type analysis_prompt
      const data = (allTemplates || []).filter((t: any) => t.type === 'analysis_prompt');
      // Map to expected format
      const mappedData = data.map((t: any) => ({
        id: t.id,
        name: t.name,
        type: t.type,
        template_content: t.templateContent
      }));
      setPromptTemplates(mappedData);

      // Set default template if none selected
      if (mappedData.length > 0 && !formData.prompt_template_id) {
        const defaultTemplate = mappedData.find((t: any) => t.name === 'Analyse CV Candidats') || mappedData[0];
        setFormData(prev => ({ ...prev, prompt_template_id: defaultTemplate.id }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des templates de prompt:', error);
    }
  };

  const loadAppelOffre = async () => {
    setInitialLoading(true);
    try {
      const data = await appelsOffresService.getById(id!);

      if (data) {
        setFormData({
          titre: data.titre || "",
          reference: data.reference || "",
          description: data.description || "",
          date_limite: data.dateLimite || "",
          date_publication: data.datePublication || "",
          date_debut_redaction_ao: data.dateDebutRedactionAo || "",
          date_qr_soumissionnaires: data.dateQrSoumissionnaires || "",
          date_reponses_qr: data.dateReponsesQr || "",
          date_analyse_offres: data.dateAnalyseOffres || "",
          date_debut_soutenance: data.dateDebutSoutenance || "",
          date_fin_soutenance: data.dateFinSoutenance || "",
          date_adjudication: data.dateAdjudication || "",
          date_contractualisation: data.dateContractualisation || "",
          ao_manager: data.aoManager || "",
          interlocuteur_da: data.interlocuteurDa || "",
          lien_ao: data.lienAo || "",
          seuil_shortlist: data.seuilShortlist || 75,
          seuil_consider: data.seuilConsider || 60,
          service_responsable_id: data.serviceResponsableId || "",
          prompt_template_id: data.promptTemplateId || "",
          statut: data.statut || "en cours",
        });
      }

      // Load evaluations
      const evaluationsData = await evaluationsService.getByAoId(id!);

      if (evaluationsData && evaluationsData.length > 0) {
        // Map to expected format
        const mappedEvaluations = evaluationsData.map((e: any) => ({
          id: e.id,
          ao_id: e.aoId,
          nom: e.nom,
          description: e.description,
          ordre: e.ordre,
          ponderation_globale_pct: e.ponderationGlobalePct
        }));
        setEvaluations(mappedEvaluations);

        const evaluationIds = mappedEvaluations
          .map((evaluation: any) => evaluation.id)
          .filter((id: any): id is string => Boolean(id));

        if (evaluationIds.length > 0) {
          // Load criteres for all evaluations
          const criteresMapLoaded: Record<number, any[]> = {};
          for (let index = 0; index < mappedEvaluations.length; index++) {
            const evaluation = mappedEvaluations[index];
            if (evaluation.id) {
              const criteresData = await criteresService.getByEvaluationId(evaluation.id);
              criteresMapLoaded[index] = (criteresData || []).map((c: any) => ({
                id: c.id,
                evaluation_id: c.evaluationId,
                nom: c.nom,
                description: c.description,
                ponderation_pct: c.ponderationPct,
                contenu_html: c.contenuHtml,
                ordre: c.ordre
              }));
            }
          }
          setCriteresMap(criteresMapLoaded);
        } else {
          setCriteresMap({});
        }
      } else {
        setCriteresMap({});
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger l'appel d'offres",
        variant: "destructive",
      });
      navigate('/appels-offres');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleAutoSave = async () => {
    if (!isEditMode || !id) return;

    try {
      const cleanFormData = {
        titre: formData.titre,
        reference: formData.reference,
        description: formData.description,
        serviceResponsableId: formData.service_responsable_id === "none" ? null : formData.service_responsable_id || null,
        dateLimite: formData.date_limite || null,
        datePublication: formData.date_publication || null,
        dateDebutRedactionAo: formData.date_debut_redaction_ao || null,
        dateQrSoumissionnaires: formData.date_qr_soumissionnaires || null,
        dateReponsesQr: formData.date_reponses_qr || null,
        dateAnalyseOffres: formData.date_analyse_offres || null,
        dateDebutSoutenance: formData.date_debut_soutenance || null,
        dateFinSoutenance: formData.date_fin_soutenance || null,
        dateAdjudication: formData.date_adjudication || null,
        dateContractualisation: formData.date_contractualisation || null,
        aoManager: formData.ao_manager,
        interlocuteurDa: formData.interlocuteur_da,
        lienAo: formData.lien_ao,
        seuilShortlist: formData.seuil_shortlist,
        seuilConsider: formData.seuil_consider,
        promptTemplateId: formData.prompt_template_id,
        statut: formData.statut
      };

      // Sauvegarder les données de l'AO
      await appelsOffresService.update(id, cleanFormData);

      // Sauvegarder les évaluations et critères
      if (evaluations.length > 0) {
        console.log('AOWizard: Sauvegarde évaluations. CriteresMap actuel:', criteresMap);

        // Capturer le criteresMap actuel avant de supprimer
        const currentCriteresMap = { ...criteresMap };

        // Supprimer les évaluations existantes (cascade vers critères)
        await evaluationsService.deleteByAoId(id);

        // Créer les nouvelles évaluations et leurs critères
        for (let i = 0; i < evaluations.length; i++) {
          const evaluation = evaluations[i];

          // Insérer l'évaluation
          const newEvaluation = await evaluationsService.create({
            aoId: id,
            nom: evaluation.nom,
            description: evaluation.description,
            ordre: i + 1,
            ponderationGlobalePct: evaluation.ponderation_globale_pct
          });

          if (!newEvaluation) {
            console.error('Erreur sauvegarde évaluation');
            continue;
          }

          // Insérer les critères de cette évaluation depuis le criteresMap capturé
          const criteres = currentCriteresMap[i] || [];
          console.log(`AOWizard: Traitement critères pour évaluation ${i}:`, criteres);

          if (criteres.length > 0 && newEvaluation) {
            for (let j = 0; j < criteres.length; j++) {
              const critere = criteres[j];
              await criteresService.create({
                evaluationId: newEvaluation.id,
                nom: critere.nom || `Critère ${j + 1}`,
                description: critere.description || '',
                ponderationPct: critere.ponderation_pct || 0,
                contenuHtml: critere.contenu_html || '',
                ordre: j + 1
              });
            }
            console.log(`${criteres.length} critères sauvegardés pour l'évaluation ${evaluation.nom}`);
          } else {
            console.log(`AOWizard: Pas de critères à sauvegarder pour évaluation ${i}`);
          }
        }
      }

      setLastSaved(new Date());
      console.log('Auto-sauvegarde réussie');
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const validateStep = (stepIndex: number): string[] => {
    const errors: string[] = [];

    switch (stepIndex) {
      case 0: // General info
        if (!formData.titre.trim()) errors.push("Le titre est requis");
        if (!formData.reference.trim()) errors.push("La référence est requise");
        break;
      
      case 1: // Evaluations
        if (evaluations.length === 0) errors.push("Au moins une évaluation est requise");
        const totalPonderation = evaluations.reduce((sum, evaluation) => sum + evaluation.ponderation_globale_pct, 0);
        if (totalPonderation !== 100) errors.push("La somme des pondérations doit être égale à 100%");
        const evaluationsInvalides = evaluations.some(evaluation => !evaluation.nom.trim());
        if (evaluationsInvalides) errors.push("Toutes les évaluations doivent avoir un nom");
        
        // Vérifier qu'il y a au moins un critère avec un nom
        const hasCriteres = Object.values(criteresMap).some(criteres => 
          criteres && criteres.length > 0 && criteres.some(c => c.nom?.trim())
        );
        if (!hasCriteres) errors.push("Au moins un critère doit être défini avec un nom");
        
        const criteresAvecErreurs = Object.entries(criteresMap).reduce<string[]>((acc, [index, criteres]) => {
          const total = (criteres || []).reduce((sum, critere) => sum + (Number(critere.ponderation_pct) || 0), 0);
          if ((criteres || []).length > 0 && Math.abs(total - 100) > 0.01) {
            const evaluationIndex = Number(index);
            const evaluationName = evaluations[evaluationIndex]?.nom?.trim()
              ? evaluations[evaluationIndex].nom
              : `Évaluation ${evaluationIndex + 1}`;
            acc.push(`${evaluationName} (${Number(total.toFixed(2))}%)`);
          }
          return acc;
        }, []);

        if (criteresAvecErreurs.length > 0) {
          errors.push(`Les pondérations des critères doivent totaliser 100% pour : ${criteresAvecErreurs.join(', ')}`);
        }
        break;

      case 2: // Config
        if (formData.seuil_shortlist <= formData.seuil_consider) {
          errors.push("Le seuil shortlist doit être supérieur au seuil consider");
        }
        break;
    }

    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (errors.length > 0) {
      toast({
        title: "Validation échouée",
        description: errors.join(", "),
        variant: "destructive",
      });
      return;
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    // Minimal validation: only title + reference required
    if (!formData.titre.trim() || !formData.reference.trim()) {
      toast({
        title: "Validation échouée",
        description: "Le titre et la référence sont requis pour sauvegarder un brouillon",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (!user) throw new Error("Utilisateur non connecté");

      const cleanFormData = {
        titre: formData.titre,
        reference: formData.reference,
        description: formData.description,
        serviceResponsableId: formData.service_responsable_id === "none" ? null : formData.service_responsable_id || null,
        dateLimite: formData.date_limite || null,
        datePublication: formData.date_publication || null,
        dateDebutRedactionAo: formData.date_debut_redaction_ao || null,
        dateQrSoumissionnaires: formData.date_qr_soumissionnaires || null,
        dateReponsesQr: formData.date_reponses_qr || null,
        dateAnalyseOffres: formData.date_analyse_offres || null,
        dateDebutSoutenance: formData.date_debut_soutenance || null,
        dateFinSoutenance: formData.date_fin_soutenance || null,
        dateAdjudication: formData.date_adjudication || null,
        dateContractualisation: formData.date_contractualisation || null,
        aoManager: formData.ao_manager,
        interlocuteurDa: formData.interlocuteur_da,
        lienAo: formData.lien_ao,
        seuilShortlist: formData.seuil_shortlist,
        seuilConsider: formData.seuil_consider,
        promptTemplateId: formData.prompt_template_id,
        statut: "en cours"
      };

      let aoId: string;

      if (isEditMode && id) {
        await appelsOffresService.update(id, cleanFormData);
        aoId = id;
      } else {
        const data = await appelsOffresService.create(cleanFormData);
        aoId = data.id;
      }

      // Save evaluations and criteria even if incomplete (skip empty names)
      const currentCriteresMap = { ...criteresMap };

      if (isEditMode && id) {
        await evaluationsService.deleteByAoId(aoId);
      }

      for (let i = 0; i < evaluations.length; i++) {
        const evaluation = evaluations[i];
        if (!evaluation.nom.trim()) continue;

        const newEvaluation = await evaluationsService.create({
          aoId: aoId,
          nom: evaluation.nom,
          description: evaluation.description,
          ordre: i + 1,
          ponderationGlobalePct: evaluation.ponderation_globale_pct
        });

        if (!newEvaluation) continue;

        const criteres = currentCriteresMap[i] || [];
        for (let j = 0; j < criteres.length; j++) {
          const critere = criteres[j];
          if (!critere.nom?.trim()) continue;

          await criteresService.create({
            evaluationId: newEvaluation.id,
            nom: critere.nom,
            description: critere.description || '',
            ponderationPct: critere.ponderation_pct || 0,
            contenuHtml: critere.contenu_html || '',
            ordre: j + 1
          });
        }
      }

      toast({
        title: "Brouillon sauvegardé",
        description: "L'appel d'offres a été sauvegardé comme brouillon",
      });

      navigate(`/appels-offres/${aoId}`);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du brouillon:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le brouillon",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    // Validate all steps
    const allErrors: string[] = [];
    for (let i = 0; i < steps.length; i++) {
      allErrors.push(...validateStep(i));
    }

    if (allErrors.length > 0) {
      toast({
        title: "Validation échouée",
        description: allErrors.join(", "),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (!user) throw new Error("Utilisateur non connecté");

      const cleanFormData = {
        titre: formData.titre,
        reference: formData.reference,
        description: formData.description,
        serviceResponsableId: formData.service_responsable_id === "none" ? null : formData.service_responsable_id || null,
        dateLimite: formData.date_limite || null,
        datePublication: formData.date_publication || null,
        dateDebutRedactionAo: formData.date_debut_redaction_ao || null,
        dateQrSoumissionnaires: formData.date_qr_soumissionnaires || null,
        dateReponsesQr: formData.date_reponses_qr || null,
        dateAnalyseOffres: formData.date_analyse_offres || null,
        dateDebutSoutenance: formData.date_debut_soutenance || null,
        dateFinSoutenance: formData.date_fin_soutenance || null,
        dateAdjudication: formData.date_adjudication || null,
        dateContractualisation: formData.date_contractualisation || null,
        aoManager: formData.ao_manager,
        interlocuteurDa: formData.interlocuteur_da,
        lienAo: formData.lien_ao,
        seuilShortlist: formData.seuil_shortlist,
        seuilConsider: formData.seuil_consider,
        promptTemplateId: formData.prompt_template_id,
        statut: formData.statut
      };

      if (isEditMode && id) {
        // Update existing
        await appelsOffresService.update(id, cleanFormData);

        // Update evaluations and their criteria
        await evaluationsService.deleteByAoId(id);

        const currentCriteresMap = { ...criteresMap };

        for (let i = 0; i < evaluations.length; i++) {
          const evaluation = evaluations[i];

          const newEvaluation = await evaluationsService.create({
            aoId: id,
            nom: evaluation.nom,
            description: evaluation.description,
            ordre: i + 1,
            ponderationGlobalePct: evaluation.ponderation_globale_pct
          });

          if (!newEvaluation) {
            console.error('Erreur sauvegarde évaluation');
            continue;
          }

          const criteres = currentCriteresMap[i] || [];
          if (criteres.length > 0) {
            for (let j = 0; j < criteres.length; j++) {
              const critere = criteres[j];
              await criteresService.create({
                evaluationId: newEvaluation.id,
                nom: critere.nom || `Critère ${j + 1}`,
                description: critere.description || '',
                ponderationPct: critere.ponderation_pct || 0,
                contenuHtml: critere.contenu_html || '',
                ordre: j + 1
              });
            }
          }
        }

        toast({
          title: "Appel d'offres modifié",
          description: "Les modifications ont été sauvegardées avec succès",
        });

        navigate(`/appels-offres/${id}`);
      } else {
        // Create new
        const data = await appelsOffresService.create(cleanFormData);

        // Save evaluations and their criteria
        const currentCriteresMap = { ...criteresMap };

        for (let i = 0; i < evaluations.length; i++) {
          const evaluation = evaluations[i];

          const newEvaluation = await evaluationsService.create({
            aoId: data.id,
            nom: evaluation.nom,
            description: evaluation.description,
            ordre: i + 1,
            ponderationGlobalePct: evaluation.ponderation_globale_pct
          });

          if (!newEvaluation) {
            console.error('Erreur sauvegarde évaluation');
            continue;
          }

          const criteres = currentCriteresMap[i] || [];
          if (criteres.length > 0) {
            for (let j = 0; j < criteres.length; j++) {
              const critere = criteres[j];
              await criteresService.create({
                evaluationId: newEvaluation.id,
                nom: critere.nom || `Critère ${j + 1}`,
                description: critere.description || '',
                ponderationPct: critere.ponderation_pct || 0,
                contenuHtml: critere.contenu_html || '',
                ordre: j + 1
              });
            }
          }
        }

        toast({
          title: "Appel d'offres créé",
          description: "L'appel d'offres a été créé avec succès",
        });

        navigate(`/appels-offres/${data.id}`);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder l'appel d'offres",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Update step statuses based on current step
  const getUpdatedSteps = () => {
    return steps.map((step, index) => ({
      ...step,
      status: index < currentStep ? 'completed' as const : 
              index === currentStep ? 'current' as const : 
              'upcoming' as const
    }));
  };

  if (initialLoading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/home')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isEditMode ? "Modifier l'analyse (AO, candidature,..)" : "Nouvelle analyse (AO, candidature,..)"}
            </h1>
            {lastSaved && (
              <p className="text-sm text-muted-foreground">
                Dernière sauvegarde: {lastSaved.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Workflow Bar */}
      <Card className="card-shadow">
        <CardContent className="p-6">
          <WorkflowBar
            currentStep={currentStep}
            steps={getUpdatedSteps()}
          />
        </CardContent>
      </Card>

      {/* Main Content */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle>{steps[currentStep].title}</CardTitle>
          <CardDescription>{steps[currentStep].description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step content */}
          {currentStep === 0 && (
            <AOGeneralStep
              formData={formData}
              setFormData={setFormData}
              services={services}
              collaborateurs={collaborateurs}
              promptTemplates={promptTemplates}
            />
          )}
          
          {currentStep === 1 && (
            <AOEvaluationsStep
              evaluations={evaluations}
              setEvaluations={setEvaluations}
              aoId={id || ''}
              criteresMap={criteresMap}
              onCriteresChange={handleCriteresChange}
            />
          )}
          
          {currentStep === 2 && (
            <AOConfigStep
              formData={formData}
              setFormData={setFormData}
              evaluations={evaluations}
              criteresMap={criteresMap}
            />
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-6 border-t">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Précédent
            </Button>

            <div className="flex space-x-2">
              <Button variant="outline" onClick={handleSaveDraft} disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? "Sauvegarde..." : "Sauvegarder brouillon"}
              </Button>
              {currentStep === steps.length - 1 ? (
                <Button
                  onClick={handleFinish}
                  disabled={loading}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? "Sauvegarde..." : (isEditMode ? "Mettre à jour" : "Créer")}
                </Button>
              ) : (
                <Button onClick={handleNext}>
                  Suivant
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}